import { proxy } from 'valtio'
import { derive, devtools } from 'valtio/utils'
import { GlobalSortDirectionEnum } from '../types/GlobalSortDirectionEnum'
import { type ApiCallType } from '../types/ApiCallType'
import { resetProxy } from '../utils/proxyUtils'

export enum CustomerTableColumnsEnum {
  Id = 'id',
  CompanyName = 'companyName',
  Name = 'firstName',
  Address = 'addressOne',
  Email = 'email',
}

export interface Pagination {
  totalCount: number
  limit: number
  offset: number
}

export interface Customer {
  id: string
  addressOne: string
  addressTwo: string
  city: string
  companyName: string
  email: null | string
  errorMessage: null | string
  firstName: string
  lastName: string
  state: string
  postalCode: string
}

export interface Filters {
  offset: number
  search: string
  sortColumn: CustomerTableColumnsEnum
  sortDirection: GlobalSortDirectionEnum
}

export interface CustomerProxyType {
  apiCall: ApiCallType
  customers: Customer[]
  selectedCustomer: Customer | null
  filters: Filters
  pagination: Pagination
  hasAppliedFilters: boolean
  hasMore: boolean
}

const initialValues = {
  apiCall: {
    error: null,
    loading: false
  },
  customers: [],
  filters: {
    offset: 0,
    search: '',
    sortColumn: CustomerTableColumnsEnum.Id,
    sortDirection: GlobalSortDirectionEnum.Descending
  },
  pagination: {
    limit: 100,
    offset: 0,
    totalCount: 0
  },
  selectedCustomer: null
}

const baseProxy = proxy(initialValues)

export const customersProxy = derive({
  hasAppliedFilters: get => {
    const { filters } = get(baseProxy)
    return Boolean(
      filters.search.length > 0
    )
  },
  hasMore: get => {
    const { customers, pagination } = get(baseProxy)
    const totalCount = pagination?.totalCount ?? 0
    return customers.length < totalCount
  }
}, { proxy: baseProxy }) as CustomerProxyType

export const resetCustomersProxy = (): void => {
  resetProxy(initialValues, customersProxy)
}

devtools(customersProxy, { name: 'customersProxy' })
